import { useCallback, useEffect, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { Content } from 'src/contexts/ContentContext/Content';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { InfoMessage } from 'src/core/POS/MessageWithIcon/InfoMessage';
import { BulkEditSeatTraitsDialog } from 'src/dialogs/BulkEdits/BulkEditSeatTraitsDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  useUserCanManageSeatSaver,
  useUserCanUpdateListing,
} from 'src/hooks/useUserHasListingPermissions';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  Feature,
  ListingNote,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_SEAT_TRAITS_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchBulkUpdateSeatTraits } from './LaunchBulkUpdateSeatTraits';

export const useBulkUpdateSeatTraits = (
  filterQuery: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const hasManageSeatSaverPermission = useUserCanManageSeatSaver();
  const hasRegularListingPermission = useUserCanUpdateListing(undefined, true);

  const hasSeatSaverFeature = useUserHasFeature(Feature.SeatSaver);

  const actionDisabled =
    (!hasManageSeatSaverPermission || !hasSeatSaverFeature) &&
    !hasRegularListingPermission;

  const updateKey = event
    ? `inventory-seat-traits-event-${event.virtId}`
    : INVENTORY_BULK_UPDATE_SEAT_TRAITS_UPDATE_KEY;

  const { showErrorDialog } = useClientLogContext();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const {
    openDialog: openBulkEditSeatTraitsDialog,
    closeDialog: closeBulkEditSeatTraitsDialog,
  } = useDialog(DialogId.BulkEditSeatTraits, BulkEditSeatTraitsDialog);

  const onSubmit = useCallback(
    async (
      seatTraits: ListingNote[] | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails,
      appendSeatTraits?: boolean
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const bulkEditClient = new BulkEditListingClient(
              activeAccountWebClientConfig
            );

            const preview =
              await bulkEditClient.bulkUpdateSeatTraitsPreview(filterQuery);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateTagsPreview', error, {
              trackErrorData: { filterQuery },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (seatTraits) {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateSeatTraits(
              {
                item1: preview!.preview,
                item2: seatTraits,
              },
              updateKey,
              supportBackgroundProcess,
              appendSeatTraits
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                refreshExpandedListItems();
              }

              closeBulkEditSeatTraitsDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdateSeatTraits', error, {
              trackErrorData: {
                preview,
                seatTraits,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      closeBulkEditSeatTraitsDialog,
      filterQuery,
      refreshExpandedListItems,
      setIsLoading,
      showErrorDialog,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkEditSeatTraitsDialog({
      header: <Content id={ContentId.UpdateSeatTraitsForPlaceholders} />,
      updateKey,
      isLoading,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditSeatTraitsDialog,
      entityType: ActionOutboxEntityType.Listing,
      infoMessage: (
        <InfoMessage
          message={<Content id={ContentId.BulkUpdatePlaceholdersMessage} />}
        />
      ),
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditSeatTraits,
        onSelect: openDialogStableRef,
      },
      disabled: actionDisabled,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditSeatTraits,
        disabled: actionDisabled,
      });
    };
  }, [actionDisabled, openDialogStableRef, registerCommand, unregisterCommand]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkEditSeatTraitsDialog({
        header: <Content id={ContentId.UpdateSeatTraits} />,
        updateKey,
        isLoading,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkEditSeatTraitsDialog,
        entityType: ActionOutboxEntityType.Listing,
        infoMessage: (
          <InfoMessage
            message={<Content id={ContentId.BulkUpdatePlaceholdersMessage} />}
          />
        ),
      });
    },
    [
      closeBulkEditSeatTraitsDialog,
      isLoading,
      onSubmit,
      openBulkEditSeatTraitsDialog,
      setIsLoading,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (
      (!hasManageSeatSaverPermission || !hasSeatSaverFeature) &&
      !hasRegularListingPermission
    ) {
      return [];
    }
    return [
      {
        title: ContentId.UpdateSeatTraits,
        disabled: isLoading,
        onClick,
      },
    ];
  }, [
    hasManageSeatSaverPermission,
    hasRegularListingPermission,
    hasSeatSaverFeature,
    isLoading,
    onClick,
  ]);

  return {
    dropDown: (
      <LaunchBulkUpdateSeatTraits
        key="updateSeatTraits"
        onClick={onClick}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
